/**
 * Procedural game audio (Phase 6, spec 0008): everything is synthesized with
 * WebAudio — no asset files, no licensing. The AudioContext is created lazily
 * on a user gesture.
 *
 * Music and the speed-driven wind were both removed (owner: the game doesn't
 * need them) — see CHANGELOG. All that remains is event audio: a jump whoosh,
 * landing thumps, and UI tones.
 */

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private volume = 0.8;

  /** Idempotent; must be called from a user gesture (click). */
  start(): void {
    if (this.ctx || typeof AudioContext === 'undefined') {
      return;
    }
    const ctx = new AudioContext();
    this.ctx = ctx;

    this.master = ctx.createGain();
    this.master.gain.value = this.volume;
    this.master.connect(ctx.destination);

    // 2s of white noise, looped — the raw material for the impact/jump sounds.
    const buffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    this.noiseBuffer = buffer;
  }

  setVolume(volume: number): void {
    this.volume = volume;
    if (this.master && this.ctx) {
      this.master.gain.setTargetAtTime(volume, this.ctx.currentTime, 0.05);
    }
  }

  /** Soft upward whoosh on jump — filtered noise sweeping up over ~150 ms. */
  jump(): void {
    if (!this.ctx || !this.master || !this.noiseBuffer) {
      return;
    }
    const ctx = this.ctx;
    const t0 = ctx.currentTime;
    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.Q.value = 0.8;
    filter.frequency.setValueAtTime(500, t0);
    filter.frequency.exponentialRampToValueAtTime(1400, t0 + 0.14);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.2, t0 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.16);
    source.connect(filter).connect(gain).connect(this.master);
    source.start(t0);
    source.stop(t0 + 0.2);
  }

  /** Landing thump; `impactSpeed` is the vertical speed lost on touchdown. */
  land(impactSpeed: number): void {
    if (!this.ctx || !this.master || !this.noiseBuffer || impactSpeed < 220) {
      return;
    }
    const ctx = this.ctx;
    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 420;
    const gain = ctx.createGain();
    const level = Math.min(1, impactSpeed / 900) * 0.5;
    gain.gain.setValueAtTime(level, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
    source.connect(filter).connect(gain).connect(this.master);
    source.start();
    source.stop(ctx.currentTime + 0.2);
  }

  click(): void {
    this.tone(880, 0.05, 0.12, 'square');
  }

  finish(): void {
    this.tone(523, 0.12, 0.2, 'triangle', 0);
    this.tone(659, 0.12, 0.2, 'triangle', 0.09);
    this.tone(784, 0.18, 0.2, 'triangle', 0.18);
  }

  private tone(
    freq: number,
    duration: number,
    level: number,
    type: OscillatorType,
    delay = 0,
  ): void {
    if (!this.ctx || !this.master) {
      return;
    }
    const ctx = this.ctx;
    const t0 = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(level, t0);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + duration);
    osc.connect(gain).connect(this.master);
    osc.start(t0);
    osc.stop(t0 + duration + 0.02);
  }
}
